import Modal from '../../components/ui/Modal'
import { printTablePayload, type CopyTableToClipboardOptions } from './copyTableToClipboard'
import { EsButton } from './ui'

type Props = {
  open: boolean
  payload: CopyTableToClipboardOptions
  onClose: () => void
}

/** Review formatted table content before opening the system print dialog. */
export function PrintPreviewModal({ open, payload, onClose }: Props) {
  const preamble = (payload.preamble ?? []).filter(
    (line) => line != null && String(line).trim() !== ''
  )

  const handlePrint = () => {
    printTablePayload(payload)
    onClose()
  }

  return (
    <Modal isOpen={open} onClose={onClose} className="max-w-4xl max-h-[90vh] flex flex-col !p-0 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-es-soft shrink-0">
        <h2 className="text-lg font-bold text-es-navy">Print preview</h2>
        <button
          type="button"
          onClick={onClose}
          className="text-es-muted hover:text-es-navy p-1 shrink-0"
          aria-label="Close"
        >
          <i className="fa-solid fa-xmark text-lg" aria-hidden />
        </button>
      </div>

      <div className="flex-1 overflow-auto px-5 py-4 min-h-0 bg-es-haze/40">
        <div className="bg-es-white border border-es-soft rounded-[var(--radius-md)] p-4 shadow-[var(--shadow-card)]">
          {preamble.map((line, i) => (
            <p key={i} className="text-sm text-es-body mb-1 last:mb-3 whitespace-pre-wrap">
              {line}
            </p>
          ))}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse min-w-[20rem]">
              <thead>
                <tr className="bg-es-haze">
                  {payload.headers.map((h) => (
                    <th
                      key={h}
                      className="px-2 py-1.5 font-semibold uppercase tracking-wider text-es-muted border border-es-soft whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payload.rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={Math.max(payload.headers.length, 1)}
                      className="px-2 py-4 text-es-muted text-center border border-es-soft"
                    >
                      No rows to print.
                    </td>
                  </tr>
                ) : (
                  payload.rows.map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) => (
                        <td
                          key={j}
                          className="px-2 py-1.5 text-es-body align-top border border-es-soft max-w-[14rem]"
                        >
                          <span className="break-words">{cell}</span>
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 px-5 py-4 border-t border-es-soft shrink-0 bg-es-white">
        <EsButton type="button" variant="secondary" onClick={onClose} className="!px-3 !py-1.5 !text-sm">
          Cancel
        </EsButton>
        <EsButton
          type="button"
          variant="primary"
          onClick={handlePrint}
          disabled={payload.rows.length === 0}
          className="!px-3 !py-1.5 !text-sm"
        >
          <i className="fa-solid fa-print mr-1.5" aria-hidden />
          Print
        </EsButton>
      </div>
    </Modal>
  )
}
